import { useState, useEffect } from "react";
import { api, formatCurrency, monthNames } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, Upload, FileSpreadsheet, Check, AlertCircle, Trash2, Plus, Calculator } from "lucide-react";

export const ImportPage = () => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importedData, setImportedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Manual CA entry form
  const [manualCA, setManualCA] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    orders_amount: "",
    ca: "",
    stock: ""
  });
  const [savingManual, setSavingManual] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2015 + 1 }, (_, i) => currentYear - i);

  const fetchImportedData = async () => {
    try {
      setLoading(true);
      const response = await api.getImportedData();
      setImportedData(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImportedData();
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        toast.error("Veuillez sélectionner un fichier Excel (.xlsx ou .xls)");
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error("Veuillez sélectionner un fichier");
      return;
    }

    try {
      setImporting(true);
      const response = await api.importExcel(file);
      const data = response.data;
      
      // Show detailed success message
      let message = data.message;
      if (data.orders_created > 0) {
        message += ` Les commandes apparaissent dans l'onglet Commandes.`;
      }
      toast.success(message);
      
      // Show errors if any
      if (data.errors && data.errors.length > 0) {
        data.errors.forEach(err => toast.warning(err));
      }
      
      setFile(null);
      fetchImportedData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteAllImports = async () => {
    try {
      setDeleting(true);
      await api.deleteAllImports();
      toast.success("Toutes les données importées ont été supprimées");
      setDeleteDialogOpen(false);
      fetchImportedData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeleting(false);
    }
  };

  const handleManualCASubmit = async (e) => {
    e.preventDefault();
    try {
      setSavingManual(true);
      await api.createManualCA({
        year: manualCA.year,
        month: manualCA.month,
        orders_amount: parseFloat(manualCA.orders_amount) || 0,
        ca: parseFloat(manualCA.ca) || 0,
        stock: parseFloat(manualCA.stock) || 0
      });
      toast.success("Données enregistrées");
      setManualCA({ ...manualCA, orders_amount: "", ca: "", stock: "" });
      fetchImportedData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSavingManual(false);
    }
  };

  return (
    <div className="space-y-6 animate-in" data-testid="import-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold">Import Excel</h1>
        <p className="text-muted-foreground mt-1">
          Importez votre historique de stock de commandes
        </p>
      </div>

      {/* Import Card */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-primary" />
            Importer un fichier Excel
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Format info */}
          <div className="bg-muted/50 p-4 rounded-sm">
            <h4 className="font-medium mb-2">Format attendu</h4>
            <p className="text-sm text-muted-foreground mb-3">
              Votre fichier Excel doit contenir les colonnes suivantes :
            </p>
            <div className="overflow-x-auto">
              <table className="text-sm border-collapse">
                <thead>
                  <tr className="bg-muted">
                    <th className="border p-2 text-left">Année</th>
                    <th className="border p-2 text-left">Mois</th>
                    <th className="border p-2 text-left">Commandes</th>
                    <th className="border p-2 text-left">Chiffre d'affaires</th>
                    <th className="border p-2 text-left">Stock de commandes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border p-2">2024</td>
                    <td className="border p-2">Janvier</td>
                    <td className="border p-2">125000</td>
                    <td className="border p-2">98000</td>
                    <td className="border p-2">85000</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* File input */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="file">Fichier Excel</Label>
              <Input
                id="file"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                data-testid="import-file-input"
              />
            </div>

            {file && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <Check className="h-4 w-4" />
                Fichier sélectionné : {file.name}
              </div>
            )}

            <Button
              onClick={handleImport}
              disabled={!file || importing}
              className="w-full sm:w-auto"
              data-testid="import-submit-btn"
            >
              {importing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Importer
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Manual CA Entry Card */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Calculator className="h-5 w-5 text-primary" />
            Saisie manuelle de CA
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleManualCASubmit} className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Année</Label>
                <Select
                  value={String(manualCA.year)}
                  onValueChange={(v) => setManualCA({ ...manualCA, year: Number(v) })}
                >
                  <SelectTrigger data-testid="manual-year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Mois</Label>
                <Select
                  value={String(manualCA.month)}
                  onValueChange={(v) => setManualCA({ ...manualCA, month: Number(v) })}
                >
                  <SelectTrigger data-testid="manual-month-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {monthNames.map((name, idx) => (
                      <SelectItem key={idx} value={String(idx + 1)}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Commandes (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0"
                  value={manualCA.orders_amount}
                  onChange={(e) => setManualCA({ ...manualCA, orders_amount: e.target.value })}
                  data-testid="manual-orders-input"
                />
              </div>
              <div className="space-y-2">
                <Label>CA réalisé (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0"
                  value={manualCA.ca}
                  onChange={(e) => setManualCA({ ...manualCA, ca: e.target.value })}
                  data-testid="manual-ca-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Stock de commandes (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0"
                  value={manualCA.stock}
                  onChange={(e) => setManualCA({ ...manualCA, stock: e.target.value })}
                  data-testid="manual-stock-input"
                />
              </div>
              <div className="flex items-end">
                <Button type="submit" disabled={savingManual} data-testid="manual-submit-btn">
                  {savingManual ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  Enregistrer
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Imported Data */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading">Données importées / saisies</CardTitle>
          {importedData.length > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setDeleteDialogOpen(true)}
              data-testid="delete-all-imports-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Tout supprimer
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : importedData.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucune donnée importée</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Année</th>
                    <th>Mois</th>
                    <th>Commandes (HT)</th>
                    <th>Chiffre d'affaires</th>
                    <th>Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {importedData.map((row) => (
                    <tr key={row.id} data-testid={`imported-row-${row.id}`}>
                      <td>{row.year}</td>
                      <td>{row.month_name || monthNames[row.month - 1]}</td>
                      <td className="currency">{formatCurrency(row.orders_amount || 0)}</td>
                      <td className="currency">{formatCurrency(row.ca)}</td>
                      <td className="currency">{formatCurrency(row.stock)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer toutes les données importées ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action va supprimer :
              <ul className="list-disc list-inside mt-2">
                <li>Toutes les données de la table ci-dessus</li>
                <li>Toutes les commandes importées (préfixe IMP-)</li>
              </ul>
              <br />
              <strong>Cette action est irréversible.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAllImports}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
              data-testid="confirm-delete-imports-btn"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Oui, tout supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ImportPage;
